import { TableStyleConfigurations } from '../config.type';
import { BorderRadiusKeys } from '../default-constants/global-styling.enum';

export const GLOBAL_TABLE_STYLING_CLASS = 'global-table-styling';

const GLOBAL_TABLE_SCOPE_SELECTOR = `#web-app finx-table.${GLOBAL_TABLE_STYLING_CLASS}`;

export function tableStyleDefaults(): TableStyleConfigurations {
  return {
    headerCss:
      'font-size: 16px; font-weight: 700; color:var(--color-on-surface-muted); padding:15px 10px 15px 24px',
    rowCss: 'padding: 1.6rem 2.4rem; font-size: 16px; font-weight: 400;',
    toolbarHeaderBgColor: 'var(--color-surface)',
    showHorizontalGridLines: true,
    showVerticalGridLines: false,
    gridLinesWidth: 1,
    rowBackgroundStyle: 'single-colour',
    rowBgColor: 'var(--color-surface)',
    oddRowBgColor: 'var(--color-surface)',
    evenRowBgColor: 'var(--color-surface)',
    borderWidth: 1,
    borderColor: 'var(--color-border-color)',
    borderRadiusClass: BorderRadiusKeys.NONE,
    borderRadiusTopLeft: 5,
    borderRadiusTopRight: 5,
    borderRadiusBottomLeft: 5,
    borderRadiusBottomRight: 5,
    borderRadiusUnit: 'px',
    backgroundColor: 'var(--color-surface)',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginUnit: 'px',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingUnit: 'px',
  };
}

function normaliseDeclarations(css: string | undefined): string {
  const trimmed = css?.trim() ?? '';
  if (!trimmed) return '';
  return trimmed.endsWith(';') ? trimmed : `${trimmed};`;
}

function rule(selector: string, ...declarations: string[]): string {
  return `${selector} { ${declarations.filter(Boolean).join(' ')} }`;
}

const RADIUS_CLASS_NAMES: Partial<Record<BorderRadiusKeys, string>> = {
  [BorderRadiusKeys.RADIUS_ONE]: 'rad-1',
  [BorderRadiusKeys.RADIUS_TWO]: 'rad-2',
};

type BorderRadiusConfig = Pick<
  TableStyleConfigurations,
  | 'borderRadiusClass'
  | 'borderRadiusTopLeft'
  | 'borderRadiusTopRight'
  | 'borderRadiusBottomLeft'
  | 'borderRadiusBottomRight'
  | 'borderRadiusUnit'
>;

// A radius class points at the theme's per-corner `--radius-rad-N-*` vars; otherwise the manual corners apply.
export function borderRadius(table: BorderRadiusConfig): string {
  const className = RADIUS_CLASS_NAMES[table.borderRadiusClass];
  if (className) {
    return ['top-left', 'top-right', 'bottom-right', 'bottom-left']
      .map((corner) => `var(--radius-${className}-${corner})`)
      .join(' ');
  }
  const unit = table.borderRadiusUnit;
  return `${table.borderRadiusTopLeft}${unit} ${table.borderRadiusTopRight}${unit} ${table.borderRadiusBottomRight}${unit} ${table.borderRadiusBottomLeft}${unit}`;
}

export function getTableRawCssString(table: TableStyleConfigurations | undefined): string {
  if (!table) return '';

  const headerRow = `${GLOBAL_TABLE_SCOPE_SELECTOR} table[hlmTable] thead[hlmTableHeader] tr[hlmTableRow]`;
  const bodyRow = `${GLOBAL_TABLE_SCOPE_SELECTOR} table[hlmTable] tbody[hlmTableBody] tr[hlmTableRow]`;
  const headerCss = normaliseDeclarations(table.headerCss);
  const rowCss = normaliseDeclarations(table.rowCss);

  const rules = [
    rule(
      `${GLOBAL_TABLE_SCOPE_SELECTOR} .finx-table-root`,
      `border-width: ${table.borderWidth}px;`,
      'border-style: solid;',
      `border-color: ${table.borderColor};`,
      `border-radius: ${borderRadius(table)};`,
      `background-color: ${table.backgroundColor};`,
      `--finx-table-grid-line-color: ${table.borderColor};`,
      `--finx-table-grid-horizontal-width: ${table.showHorizontalGridLines ? table.gridLinesWidth : 0}px;`,
      `--finx-table-grid-vertical-width: ${table.showVerticalGridLines ? table.gridLinesWidth : 0}px;`,
      `margin: ${table.marginTop}${table.marginUnit} ${table.marginRight}${table.marginUnit} ${table.marginBottom}${table.marginUnit} ${table.marginLeft}${table.marginUnit};`,
      `padding: ${table.paddingTop}${table.paddingUnit} ${table.paddingRight}${table.paddingUnit} ${table.paddingBottom}${table.paddingUnit} ${table.paddingLeft}${table.paddingUnit};`,
    ),
    rule(
      `${GLOBAL_TABLE_SCOPE_SELECTOR} .finx-table-toolbar`,
      `background-color: ${table.toolbarHeaderBgColor};`,
    ),
  ];

  // Background comes before the authored css so the authored css can override it. With
  // alternating rows the header counts as the row before the first (odd) body row.
  if (table.rowBackgroundStyle === 'alternate-colours') {
    rules.push(
      rule(headerRow, `background-color: ${table.evenRowBgColor};`, headerCss),
      rule(`${bodyRow}:nth-child(odd)`, `background-color: ${table.oddRowBgColor};`, rowCss),
      rule(`${bodyRow}:nth-child(even)`, `background-color: ${table.evenRowBgColor};`, rowCss),
    );
  } else {
    rules.push(
      ...(headerCss ? [rule(headerRow, headerCss)] : []),
      rule(bodyRow, `background-color: ${table.rowBgColor};`, rowCss),
    );
  }

  return `\n${rules.join('\n')}\n`;
}
